import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Home, 
  CheckCircle, 
  Users, 
  Calculator, 
  FileText, 
  Shield,
  TrendingUp,
  Euro,
  Clock,
  MapPin,
  Key,
  Phone,
  Award
} from "lucide-react";
import janineHardiImage from "@/assets/janine-hardi.jpg";
import wohnrechtImage from "@/assets/wohnrecht.png";
import { Link } from "react-router-dom";

const Wohnrecht = () => {
  const advantages = [
    {
      icon: Euro,
      title: "Einmalzahlung oder Zeitrente",
      description: "Eigentümer können wählen, ob sie den Verkaufserlös als hohe Einmalzahlung oder als regelmäßige Zeitrente erhalten."
    },
    {
      icon: Shield,
      title: "Sicherheit",
      description: "Das Wohnrecht wird notariell im Grundbuch gesichert. So ist sichergestellt, dass niemand den Bewohner aus der Immobilie verdrängen kann."
    },
    {
      icon: Key,
      title: "Eigener Herr sein",
      description: "Trotz Verkauf bleibt der Alltag unverändert. Man wohnt weiterhin im vertrauten Zuhause – ohne das Gefühl, 'nur Mieter' zu sein."
    },
    {
      icon: TrendingUp,
      title: "Flexible Gestaltung",
      description: "Wohnrechte können mit Leibrente, Teilverkauf oder Schenkung kombiniert werden."
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Kostenlose Erstberatung",
      description: "Individuelle Fragen werden geklärt: Einmalzahlung, Zeitrente, Kombination mit Leibrente oder Teilverkauf."
    },
    {
      step: "2", 
      title: "Wertermittlung",
      description: "Die Immobilie wird professionell bewertet, um eine faire Auszahlung zu berechnen."
    },
    {
      step: "3",
      title: "Vertragsgestaltung", 
      description: "Das Wohnrecht wird notariell gesichert. Optionen: lebenslang oder befristet."
    },
    {
      step: "4",
      title: "Auszahlung",
      description: "Je nach Modell erfolgt die Auszahlung als Einmalzahlung oder in Raten."
    }
  ];

  const examples = [
    {
      title: "Lebenslanges Wohnrecht + Einmalzahlung",
      property: "Einfamilienhaus",
      value: "700.000 €",
      payout: "300.000 €",
      benefit: "Wohnrecht lebenslang gesichert"
    },
    {
      title: "Wohnrecht mit Zeitrente", 
      property: "Eigentumswohnung",
      value: "400.000 €",
      payout: "1.200 € monatlich für 20 Jahre",
      benefit: "Wohnrecht bis zum Ende der Laufzeit gesichert"
    },
    {
      title: "Kombination Wohnrecht & Teilverkauf",
      property: "Einfamilienhaus",
      value: "900.000 €", 
      payout: "360.000 € (40% Verkauf)",
      benefit: "Restwert bleibt, Wohnrecht lebenslang gesichert"
    }
  ];

  const faqs = [
    {
      question: "Ist mein Wohnrecht sicher?",
      answer: "Ja, es ist im Grundbuch eingetragen und kann nicht einfach aufgehoben werden."
    },
    {
      question: "Kann ich mein Wohnrecht verkaufen?",
      answer: "Nein, es ist höchstpersönlich und nicht übertragbar."
    },
    {
      question: "Muss ich Nebenkosten zahlen?", 
      answer: "Ja, wie bei einem Eigentümer/Mieter üblich (z. B. Strom, Heizung). Größere Instandhaltungen trägt in der Regel der neue Eigentümer."
    },
    {
      question: "Was passiert mit meinen Erben?",
      answer: "Die Immobilie gehört nicht mehr zur Erbmasse – das Wohnrecht endet mit dem Tod des Berechtigten."
    },
    {
      question: "Kann das Wohnrecht mit anderen Modellen kombiniert werden?",
      answer: "Ja, z. B. mit Leibrente, Teilverkauf oder Nießbrauch."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wohnrecht: Grundbuchlich gesichertes Wohnrecht | eigenheimverrentung.de</title>
        <meta 
          name="description" 
          content="Wohnrecht: Haus verkaufen & mit grundbuchlich gesichertem Wohnrecht wohnen bleiben. ✓ Einmalzahlung oder Zeitrente ✓ Kostenlose Beratung zur Eigenheimverrentung" 
        />
        <meta name="keywords" content="wohnrecht, grundbuchlich gesichertes wohnrecht, haus verkaufen und wohnen bleiben, verkaufen aber wohnen bleiben, einmalzahlung, zeitrente, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/wohnrecht" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Wohnrecht: Grundbuchlich gesichertes Wohnrecht" />
        <meta property="og:description" content="Wohnen bleiben mit grundbuchlich gesichertem Wohnrecht. Kapital freisetzen und trotzdem im eigenen Zuhause bleiben." />
        <meta property="og:url" content="https://eigenheimverrentung.de/wohnrecht" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Wohnrecht: Grundbuchlich gesichertes Wohnrecht" />
        <meta name="twitter:description" content="Wohnen bleiben mit grundbuchlich gesichertem Wohnrecht. Kapital freisetzen und im eigenen Zuhause bleiben." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Wohnrecht – Grundbuchlich gesichertes Wohnrecht",
            "description": "Kompletter Ratgeber zum Wohnrecht: Haus verkaufen und mit grundbuchlich gesichertem Wohnrecht wohnen bleiben.",
            "author": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de"
            },
            "publisher": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de",
              "url": "https://eigenheimverrentung.de"
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://eigenheimverrentung.de/wohnrecht"
            }
          })}
        </script>
        
        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Ist mein Wohnrecht sicher?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, es ist im Grundbuch eingetragen und kann nicht einfach aufgehoben werden."
                }
              },
              {
                "@type": "Question",
                "name": "Kann ich mein Wohnrecht verkaufen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nein, es ist höchstpersönlich und nicht übertragbar."
                }
              },
              {
                "@type": "Question",
                "name": "Muss ich Nebenkosten zahlen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, wie bei einem Eigentümer/Mieter üblich (z. B. Strom, Heizung). Größere Instandhaltungen trägt in der Regel der neue Eigentümer."
                }
              },
              {
                "@type": "Question",
                "name": "Was passiert mit meinen Erben?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Die Immobilie gehört nicht mehr zur Erbmasse – das Wohnrecht endet mit dem Tod des Berechtigten."
                }
              },
              {
                "@type": "Question",
                "name": "Kann das Wohnrecht mit anderen Modellen kombiniert werden?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, z. B. mit Leibrente, Teilverkauf oder Nießbrauch."
                }
              }
            ]
          })}
        </script>

        {/* Organization & Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": "Deutschland",
            "serviceType": ["Leibrente", "Wohnrecht", "Nießbrauchrecht", "Teilverkauf", "Rückmiete", "Seniorenfinanzierung"],
            "description": "Kostenlose Beratung zur Eigenheimverrentung mit Wohnrecht - Haus verkaufen und wohnen bleiben."
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Wohnrecht",
                "item": "https://eigenheimverrentung.de/wohnrecht"
              }
            ]
          })}
        </script>

        {/* HowTo Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Wie erhalte ich ein grundbuchlich gesichertes Wohnrecht?",
            "description": "Schritt-für-Schritt Anleitung zum Wohnrecht - Haus verkaufen und wohnen bleiben",
            "totalTime": "P21D",
            "step": processSteps.map((step, index) => ({
              "@type": "HowToStep",
              "position": index + 1,
              "name": step.title,
              "text": step.description
            }))
          })}
        </script>

        {/* Service Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Wohnrecht",
            "name": "Wohnrecht - Grundbuchlich gesichertes Wohnrecht",
            "description": "Wohnen bleiben mit grundbuchlich gesichertem Wohnrecht. Kapital freisetzen durch Einmalzahlung oder Zeitrente.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "email": "info@renteplusimmobilie.de"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Senioren die sicher im eigenen Zuhause bleiben möchten"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Wohnrecht Leistungen",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Lebenslanges Wohnrecht",
                    "description": "Grundbuchlich gesichertes Wohnrecht bis ans Lebensende"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Einmalzahlung oder Zeitrente",
                    "description": "Flexible Auszahlungsoptionen nach Wunsch"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Kombinierbar mit anderen Modellen",
                    "description": "Kombination mit Leibrente, Teilverkauf oder Nießbrauch möglich"
                  }
                }
              ]
            },
            "url": "https://eigenheimverrentung.de/wohnrecht"
          })}
        </script>
      </Helmet>
      
      <Header />
      
      <main>
        {/* Breadcrumb Navigation */}
        <section className="py-4 bg-muted/30 border-b">
          <div className="container-comfortable">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">
                Startseite
              </Link>
              <span>/</span>
              <span className="text-primary font-medium">Wohnrecht</span>
            </nav>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-subtle">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-base px-4 py-2">
                Kostenlose Beratung
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6 text-balance">
                Wohnrecht – Sicher wohnen bleiben
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 text-balance">
                Wohnen bleiben mit grundbuchlich gesichertem Wohnrecht
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-xl px-8" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
                  <Calculator className="h-5 w-5 mr-2" />
                  Kostenloses Telefongespräch
                </Button>
                <Button variant="outline" size="lg" className="text-xl px-8" onClick={() => window.open('tel:089628246446', '_self')}>
                  <Phone className="h-5 w-5 mr-2" />
                  089 62 82 46 46
                </Button>
              </div>
            </div>
          </div>
        </section>
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="container-comfortable relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="secondary" className="mb-6 text-lg px-6 py-2">
              <Home className="mr-2 h-5 w-5" />
              Sicher wohnen bleiben
            </Badge>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary mb-6 text-balance">
              Wohnrecht – Kostenlose Beratung
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 text-balance max-w-3xl mx-auto">
              Wohnen bleiben mit grundbuchlich gesichertem Wohnrecht
            </p>

            <p className="text-lg text-muted-foreground mb-12 max-w-4xl mx-auto leading-relaxed">
              Viele Senioren möchten ihre Immobilie zu Geld machen, aber gleichzeitig unbedingt in den eigenen vier Wänden bleiben. Die Lösung: Wohnrecht mit Kapitalfreisetzung. Das Wohnrecht wird notariell im Grundbuch eingetragen und garantiert, dass der bisherige Eigentümer lebenslang oder zeitlich befristet in seiner Immobilie wohnen bleiben darf.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-xl px-12 py-6">
                <Calculator className="mr-2 h-6 w-6" />
                Kostenlose Beratung
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-12 md:py-16">
        <div className="container-comfortable">
          <div className="max-w-2xl mx-auto text-center">
            <img 
              src={wohnrechtImage} 
              alt="Wohnrecht - Grundbuchlich gesichertes Wohnrecht" 
              className="w-full max-w-md mx-auto rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Was ist Wohnrecht */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
              Was ist ein Wohnrecht?
            </h2>
            
            <Card className="card-elevated mb-8">
              <CardContent className="p-8">
                <p className="text-lg mb-6 text-muted-foreground leading-relaxed">
                  Das Wohnrecht ist ein dingliches Recht, das im Grundbuch eingetragen wird. Es gibt dem Berechtigten das Recht, eine Immobilie zu bewohnen – unabhängig von Eigentümerwechseln.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-4">Typische Varianten:</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-primary mb-1">Lebenslanges Wohnrecht</p>
                      <p className="text-muted-foreground">Sicherheit bis ans Lebensende.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-primary mb-1">Befristetes Wohnrecht</p>
                      <p className="text-muted-foreground">z. B. für 10, 15 oder 20 Jahre.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-accent/10 p-6 rounded-lg">
                  <p className="text-muted-foreground leading-relaxed">
                    In Verbindung mit einem Immobilienverkauf können Eigentümer gleichzeitig Kapital freisetzen – sei es durch eine Einmalzahlung oder durch eine Zeitrente.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            Vorteile von Wohnrecht
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {advantages.map((advantage, index) => (
              <Card key={index} className="card-elevated h-full">
                <CardHeader className="text-center">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <advantage.icon className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl text-primary">
                    {advantage.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    {advantage.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Für wen geeignet */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
              Für wen eignet sich das Wohnrecht?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Euro className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Eigentümer mit Kapitalbedarf</h3>
                      <p className="text-muted-foreground">Die Liquidität freisetzen wollen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Shield className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Senioren mit Sicherheitsbedürfnis</h3>
                      <p className="text-muted-foreground">Die lebenslang wohnen bleiben wollen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Menschen ohne hohe Kosten</h3>
                      <p className="text-muted-foreground">Die keine hohen laufenden Kosten tragen wollen, aber Kapital aus der Immobilie benötigen.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Users className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Flexible Familien</h3>
                      <p className="text-muted-foreground">Die flexibel gestalten möchten, ob Erben noch beteiligt bleiben.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Der Ablauf bei renteplusimmobilie.de
            </h2>

            <div className="space-y-6">
              {processSteps.map((step, index) => (
                <Card key={index} className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0">
                        {step.step}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-primary mb-2">{step.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beispielrechnungen */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
            Beispielrechnungen & Praxisfälle
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {examples.map((example, index) => (
              <Card key={index} className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-xl text-primary">{example.title}</CardTitle>
                  <Badge variant="outline" className="w-fit">{example.property}</Badge>
                  <div className="text-2xl font-bold text-accent">{example.value}</div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-muted-foreground mb-1">Auszahlung:</p>
                    <p className="font-semibold text-lg">{example.payout}</p>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm font-medium text-primary">→ {example.benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Unterschiede zu anderen Modellen */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Unterschiede zu anderen Verrentungsmodellen
            </h2>

            <div className="space-y-6">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Home className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Wohnrecht vs. Nießbrauch</h3>
                      <p className="text-muted-foreground">Nießbrauch erlaubt auch Vermietung, Wohnrecht nur eigene Nutzung.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Wohnrecht vs. Leibrente</h3>
                      <p className="text-muted-foreground">Leibrente zahlt lebenslang, Wohnrecht kann befristet oder einmalig vergütet sein.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Wohnrecht vs. Rückmiete</h3>
                      <p className="text-muted-foreground">Rückmiete bringt komplette Auszahlung + Mietvertrag, beim Wohnrecht entfällt Miete.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-accent/10 p-3 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Wohnrecht vs. Teilverkauf</h3>
                      <p className="text-muted-foreground">Beim Teilverkauf bleibt man Miteigentümer, beim Wohnrecht nicht.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-12 text-center">
              Häufige Fragen zu Wohnrecht (FAQ)
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="card-elevated">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-primary mb-3 text-lg">{faq.question}</h3>
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Warum renteplusimmobilie.de */}
      <section className="py-16 md:py-24 bg-accent/5">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto text-center">
            {/* Founder Image */}
            <div className="mb-8">
              <img 
                src={janineHardiImage} 
                alt="Janine Hardi - Gründerin von RentePlusImmobilie" 
                className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto shadow-lg"
              />
              <p className="text-sm text-muted-foreground mt-3">Janine Hardi, Gründerin von RentePlusImmobilie</p>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
              Warum RentePlusImmobilie
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Shield className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Kompetente Beratung</h3>
                      <p className="text-muted-foreground">Spezialisten für alle Verrentungsmodelle.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Kostenlos & unverbindlich</h3>
                      <p className="text-muted-foreground">Keine versteckten Gebühren.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Key className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Sicherheit im Fokus</h3>
                      <p className="text-muted-foreground">Wohnrecht wird rechtlich wasserdicht abgesichert.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Users className="h-8 w-8 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Individuelle Lösungen</h3>
                      <p className="text-muted-foreground">Maßgeschneiderte Konzepte für Kapitalbedarf & Sicherheit.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Fazit & CTA */}
      <section className="py-16 md:py-24">
        <div className="container-comfortable">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8">
              Fazit – Wohnrecht als sichere Lösung für Kapital & Wohnen
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Das Wohnrecht bietet die perfekte Kombination aus Kapitalfreisetzung und Sicherheit:
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Euro className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Man erhält sofortige Liquidität oder eine regelmäßige Rente.</p>
                </CardContent>
              </Card>
              
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Home className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Gleichzeitig bleibt man lebenslang oder befristet im eigenen Zuhause.</p>
                </CardContent>
              </Card>
              
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <Shield className="h-8 w-8 text-accent mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Alles ist rechtlich abgesichert durch den Grundbucheintrag.</p>
                </CardContent>
              </Card>
            </div>

            <p className="text-lg text-muted-foreground mb-12 leading-relaxed">
              Mit der kostenlosen Beratung von renteplusimmobilie.de können Eigentümer prüfen, ob Wohnrecht, Leibrente, Teilverkauf, Rückmiete oder Nießbrauch die richtige Lösung ist.
            </p>

            <div className="bg-accent/10 p-8 rounded-xl mb-12">
              <p className="text-lg mb-4">
                👉 <strong>Wer Kapital benötigt und gleichzeitig Sicherheit im Alter haben möchte, sollte das Wohnrecht als Verrentungsmodell prüfen.</strong>
              </p>
            </div>

            <Button size="lg" className="text-xl px-12 py-6" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
              <Calculator className="mr-2 h-6 w-6" />
              Jetzt kostenlose Beratung anfragen
            </Button>
          </div>
        </div>
      </section>

      </main>

      <ContactSection />
      <HeyflowBanner />
    </div>
  );
};

export default Wohnrecht;